import { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Clock, FileText, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface AuditEvent {
  status: string;
  previous_status: string | null;
  status_changed_at: string;
  status_changed_by: string;
  user_name?: string;
  payment_amount?: number;
}

interface AuditHistoryTimelineProps {
  history: AuditEvent[];
}

export function AuditHistoryTimeline({ history }: AuditHistoryTimelineProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    // Show relative time for recent changes (last 24 hours)
    if (diffHours < 24) {
      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return diffMinutes <= 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
      }
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }

    // Otherwise show formatted date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return <Check className="h-4 w-4" />;
      case 'sent':
        return <FileText className="h-4 w-4" />;
      case 'draft':
        return <Clock className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getTransitionText = (event: AuditEvent) => {
    if (!event.previous_status) {
      return `Created as ${getStatusLabel(event.status)}`;
    }

    if (event.payment_amount) {
      return `Payment received: ${formatCurrency(event.payment_amount)} → ${getStatusLabel(event.status)}`;
    }

    return `${getStatusLabel(event.previous_status)} → ${getStatusLabel(event.status)}`;
  };

  if (history.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Audit History</h3>
          <span className="text-sm text-muted-foreground">No changes</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="space-y-3">
        <Button
          variant="ghost"
          className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label="Audit History"
        >
          <h3 className="text-sm font-medium">Audit History</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {history.length} {history.length === 1 ? 'change' : 'changes'}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" data-icon="chevron-up" />
            ) : (
              <ChevronDown className="h-4 w-4" data-icon="chevron-down" />
            )}
          </div>
        </Button>

        {isExpanded && (
          <div className="space-y-4 pt-2">
            {history.map((event, index) => (
              <div
                key={index}
                className="flex gap-3"
                data-testid="timeline-item"
                data-status={event.status}
              >
                {/* Timeline Dot */}
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'rounded-full p-1.5',
                      event.status === 'paid' && 'bg-green-100 text-emerald-600',
                      event.status === 'sent' && 'bg-blue-100 text-sky-600',
                      event.status === 'draft' && 'bg-gray-100 text-gray-600'
                    )}
                    data-testid="timeline-dot"
                  >
                    {getStatusIcon(event.status)}
                  </div>
                  {index < history.length - 1 && (
                    <div className="w-px h-full bg-gray-200 flex-1 mt-1" />
                  )}
                </div>

                {/* Event Details */}
                <div className="flex-1 pb-4">
                  <p className="text-sm font-medium">{getTransitionText(event)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {event.user_name || 'Unknown User'} • {formatTimestamp(event.status_changed_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
